import React, { FC } from 'react';
import styled from 'styled-components';
import Colours from '../../../design-system/colours';
import Variables from '../../../design-system/variables';
import GaugeChart from 'react-gauge-chart';
import { useTranslation } from 'react-i18next';
import InfoIcon from '@mui/icons-material/Info';
import { Tooltip } from '@mui/material';

export interface OvNpsTrendProps {
  netPromoterScore?: number;
}

const OvNpsTrend: FC<OvNpsTrendProps> = ({ netPromoterScore }) => {
  const { t } = useTranslation();

  const mapToPercentage = (value: number): number => {
    const minValue = -100;
    const maxValue = 100;
    const clamped = Math.min(maxValue, Math.max(minValue, value));
    return (clamped + maxValue) / 2 / 100;
  };

  return (
    <Container>
      <ContentRow>
        <Tooltip title={t('npsTrends.description')}>
          <StyledInfoIcon />
        </Tooltip>
        <Title>{t('npsTrends.title')} = </Title>
        <PromotersBox>
          <PromotersPercentageBox>%</PromotersPercentageBox>
          {t('npsValues.promoters')}
        </PromotersBox>{' '}
        -
        <DetractorsBox>
          <DetractorsPercentageBox>%</DetractorsPercentageBox>
          {t('npsValues.detractors')}
        </DetractorsBox>
      </ContentRow>
      {netPromoterScore && (
        <ContentRow>
          <ChartContainer>
            <GaugeChart
              colors={[Colours.OV_VIVID_BLUE]}
              animate={false}
              nrOfLevels={1}
              percent={mapToPercentage(netPromoterScore)}
              formatTextValue={(value) => netPromoterScore?.toFixed(0)}
              needleColor={Colours.OV_BASE}
              needleBaseColor={Colours.OV_BASE}
              textColor={Colours.OV_BASE}
            />
          </ChartContainer>
        </ContentRow>
      )}
    </Container>
  );
};

export default OvNpsTrend;

const Container = styled.div`
  background-color: ${Colours.OV_LIGHT_GRAY};
  padding: 1rem;
  border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
`;

const ContentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  border-bottom: 1px solid lightgray;
  margin-bottom: 1rem;
  padding-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: 0;
  }
`;

const Title = styled.h3`
  margin: 0;
  color: ${Colours.OV_BASE_LIGHT};
`;

const ValueBox = styled.div`
  padding: 0.75rem;
  border-radius: ${Variables.borderRadius.SMALL};
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const PromotersBox = styled(ValueBox)`
  background-color: lightblue;
`;

const DetractorsBox = styled(ValueBox)`
  background-color: lightpink;
`;

const PercentageBox = styled.div`
  padding: 0.25rem;
  border-radius: 50%;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PromotersPercentageBox = styled(PercentageBox)`
  background-color: darkblue;
  color: ${Colours.OV_WHITE};
`;

const DetractorsPercentageBox = styled(PercentageBox)`
  background-color: ${Colours.OV_RED};
`;

const StyledInfoIcon = styled(InfoIcon)`
  && {
    width: 1rem;
    height: 1rem;
  }
`;

const ChartContainer = styled.div`
  width: 20rem;
  max-width: 100%;
`;
